import { useEffect, useState } from 'react'
import { Button } from '../../components/atoms/Button'
import { Checkbox } from '../../components/atoms/Checkbox'
import { Input } from '../../components/atoms/Input'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'
import type {
  CustomDataTypeDto,
  CustomDataTypeValueDto
} from '../../../viewmodels/useCustomDataTypes'

const TYPE_NAME_MAX = 128
const VALUE_MAX = 256

interface Props {
  readonly item: CustomDataTypeDto
  readonly expanded: boolean
  readonly onToggle: () => void
  /**
   * Single update path — Save in the header builds a sparse patch with
   * only the fields that changed (name OR is_axes OR both) and fires
   * one updateType call. Per Kiran 2026-05-18: the checkbox is a UI
   * draft until Save is clicked.
   */
  readonly onUpdateType: (
    id: string,
    patch: { type_name?: string; is_axes?: boolean }
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly onDeleteType: (id: string) => Promise<void>
  readonly onAddValue: (typeId: string, value: string) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly onRenameValue: (valueId: string, newValue: string) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly onDeleteValue: (valueId: string) => Promise<void>
}

/**
 * CustomDataTypeCard — one card per custom data type.
 *
 * Header layout (always editable, no separate edit mode):
 *   [name input] [☐ Is axes] [Save] [Delete] [▼ expand]
 *
 * Save is disabled until either the name or the "Is axes" checkbox
 * differs from the server-side row. Clicking Save fires ONE
 * updateType call carrying only the changed fields.
 *
 * Expanded body holds the values list + add-value input. The
 * "Is axes" flag is NOT in the expanded body — it lives in the
 * header so the operator can flip it without expanding.
 */
export function CustomDataTypeCard({
  item,
  expanded,
  onToggle,
  onUpdateType,
  onDeleteType,
  onAddValue,
  onRenameValue,
  onDeleteValue
}: Props): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.MANAGECUSTOMDATATYPES, 'edit')
  const canDelete = can(SCREENCODES.MANAGECUSTOMDATATYPES, 'delete')
  const canAdd = can(SCREENCODES.MANAGECUSTOMDATATYPES, 'add')
  // Header drafts — local state until Save is clicked.
  const [draftName, setDraftName] = useState<string>(item.type_name)
  const [draftIsAxes, setDraftIsAxes] = useState<boolean>(item.is_axes)
  const [saving, setSaving] = useState<boolean>(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // When the server-side row changes, reset the draft so the inputs
  // reflect the new truth — BUT only when the operator isn't actively
  // editing. The `dirty` guard preserves unsaved typing if the
  // underlying item shifts in the background (a sibling mutation, a
  // polled refresh, another tab). Per SOP 02 §"Mutation Patterns
  // (LOCKED)" — DRAFT PRESERVATION.
  useEffect(() => {
    if (!dirty) {
      setDraftName(item.type_name)
      setDraftIsAxes(item.is_axes)
      setSaveError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.type_name, item.is_axes])

  // Values-side state (unchanged from prior version).
  const [renamingValueId, setRenamingValueId] = useState<string | null>(null)
  const [renameValueInput, setRenameValueInput] = useState('')
  const [renameValueError, setRenameValueError] = useState<string | null>(null)
  const [newValueInput, setNewValueInput] = useState('')
  const [addValueError, setAddValueError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const trimmedName = draftName.trim()
  const nameChanged = trimmedName !== item.type_name && trimmedName.length > 0
  const axesChanged = draftIsAxes !== item.is_axes
  const dirty = nameChanged || axesChanged

  /**
   * Structural lock — once any active product depends on this type,
   * the operator can ONLY rename. is_axes, value adds, value
   * deletes, and type delete all become 409 server-side. UI mirrors
   * that here so disabled controls + a tooltip explain why. Per
   * Kiran 2026-05-18.
   */
  const structuralLock = item.linked_product_count > 0
  const lockTooltip = structuralLock
    ? `Locked: ${item.linked_product_count} active product${item.linked_product_count === 1 ? '' : 's'} use${item.linked_product_count === 1 ? 's' : ''} this type. Rename is still allowed.`
    : ''

  const handleSave = async (): Promise<void> => {
    if (!dirty || saving) return
    setSaving(true)
    setSaveError(null)
    const patch: { type_name?: string; is_axes?: boolean } = {}
    if (nameChanged) patch.type_name = trimmedName
    if (axesChanged) patch.is_axes = draftIsAxes
    const res = await onUpdateType(item.id, patch)
    setSaving(false)
    if (!res.ok) setSaveError(res.message)
    // On success the useEffect above resyncs the draft from the new item.
  }

  const startRenameValue = (val: CustomDataTypeValueDto): void => {
    setRenamingValueId(val.id)
    setRenameValueInput(val.value)
    setRenameValueError(null)
  }
  const cancelRenameValue = (): void => {
    setRenamingValueId(null)
    setRenameValueInput('')
    setRenameValueError(null)
  }
  const commitRenameValue = async (): Promise<void> => {
    const id = renamingValueId
    if (id === null) return
    const trimmed = renameValueInput.trim()
    if (trimmed === '') {
      cancelRenameValue()
      return
    }
    setBusy(true)
    setRenameValueError(null)
    const res = await onRenameValue(id, trimmed)
    setBusy(false)
    if (res.ok) {
      cancelRenameValue()
    } else {
      setRenameValueError(res.message)
    }
  }

  const addValue = async (): Promise<void> => {
    const trimmed = newValueInput.trim()
    if (trimmed === '') return
    setBusy(true)
    setAddValueError(null)
    const res = await onAddValue(item.id, trimmed)
    setBusy(false)
    if (res.ok) {
      setNewValueInput('')
    } else {
      setAddValueError(res.message)
    }
  }

  const handleDeleteType = async (): Promise<void> => {
    if (typeof window !== 'undefined' && !window.confirm('Are you sure you want to delete?')) return
    await onDeleteType(item.id)
  }

  const handleDeleteValue = async (val: CustomDataTypeValueDto): Promise<void> => {
    if (typeof window !== 'undefined' && !window.confirm('Are you sure you want to delete?')) return
    await onDeleteValue(val.id)
  }

  return (
    <div className="border border-border rounded-md bg-bg-primary overflow-hidden">
      {/* ── Header — name + "Is axes" checkbox + Save + Delete + expand ─── */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-parchment/30 transition"
        onClick={onToggle}
      >
        {/* Name input + "Is axes" checkbox — clicks inside don't toggle expand */}
        <div
          className="flex flex-wrap items-center gap-3 min-w-0 flex-1"
          onClick={(e) => e.stopPropagation()}
        >
          <Input
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && dirty) { e.preventDefault(); void handleSave() }
            }}
            maxLength={TYPE_NAME_MAX}
            // Floor for finger tap + readability on mobile.
            className="min-w-[10rem] font-heading text-h4"
            aria-label="Type name"
          />
          <label
            className={`flex items-center gap-2 ${structuralLock ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            title={lockTooltip}
          >
            <Checkbox
              checked={draftIsAxes}
              disabled={structuralLock}
              onChange={(e) => setDraftIsAxes(e.target.checked)}
            />
            <span className="font-body text-body-sm text-text-primary whitespace-nowrap">
              Is axes
            </span>
          </label>
        </div>

        <div
          className="flex items-center gap-2 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {item.linked_product_count > 0 ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-bamboo/20 text-charcoal font-medium text-body-xs"
              title="Number of active products that depend on this type. Structural edits lock when > 0."
            >
              {item.linked_product_count} product{item.linked_product_count === 1 ? '' : 's'}
            </span>
          ) : null}
          <span className="font-body text-body-sm text-text-secondary">
            {item.values.length} value{item.values.length === 1 ? '' : 's'}
          </span>
          {canEdit ? (
            <Button
              size="sm"
              variant="primary"
              onClick={() => void handleSave()}
              disabled={!dirty || saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          ) : null}
          {canDelete ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void handleDeleteType()}
              disabled={busy || saving || structuralLock}
              title={structuralLock ? lockTooltip : ''}
            >
              Delete
            </Button>
          ) : null}
          <button
            type="button"
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={onToggle}
            className="text-text-secondary px-1"
          >
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {/* Save error sits just under the header, full-width */}
      {saveError !== null ? (
        <p className="px-4 pb-2 text-body-xs font-body text-terra">{saveError}</p>
      ) : null}

      {/* ── Expanded body — values list + add-value input ──────────── */}
      {expanded ? (
        <div className="px-4 pb-4 pt-1 border-t border-border bg-parchment/20">
          {item.values.length === 0 ? (
            <p className="font-body text-body-sm text-text-secondary py-3">
              No values yet. Add one below.
            </p>
          ) : (
            <ul className="space-y-2 py-2">
              {item.values.map((val) => {
                const isRenamingThis = renamingValueId === val.id
                return (
                  <li key={val.id} className="flex items-center gap-3">
                    {isRenamingThis ? (
                      <div className="flex-1 min-w-0">
                        <Input
                          value={renameValueInput}
                          onChange={(e) => setRenameValueInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); void commitRenameValue() }
                            if (e.key === 'Escape') { e.preventDefault(); cancelRenameValue() }
                          }}
                          onBlur={() => void commitRenameValue()}
                          maxLength={VALUE_MAX}
                          autoFocus
                          className="min-w-[10rem]"
                        />
                        {renameValueError !== null ? (
                          <span className="block text-body-xs font-body text-terra mt-1">
                            {renameValueError}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <span className="font-body text-body text-charcoal flex-1 truncate">{val.value}</span>
                    )}
                    {!isRenamingThis ? (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Rename is always allowed — even when the
                            type is locked structurally. Per the lock
                            rule: rename only, no structural change. */}
                        {canEdit ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => startRenameValue(val)}
                          >
                            Rename
                          </Button>
                        ) : null}
                        {canDelete ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void handleDeleteValue(val)}
                            disabled={busy || structuralLock}
                            title={structuralLock ? lockTooltip : ''}
                          >
                            Delete
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          )}

          {/* Add value inline — disabled when the type is locked
              (any active category references it). Tooltip explains. */}
          {canAdd ? (
            <>
              <div className="flex items-center gap-2 mt-3" title={structuralLock ? lockTooltip : ''}>
                <Input
                  value={newValueInput}
                  onChange={(e) => setNewValueInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !structuralLock) { e.preventDefault(); void addValue() }
                  }}
                  placeholder={structuralLock ? 'Locked — type is in use' : 'Add a value…'}
                  maxLength={VALUE_MAX}
                  disabled={structuralLock}
                  className="min-w-[10rem]"
                />
                <Button
                  onClick={() => void addValue()}
                  disabled={busy || newValueInput.trim() === '' || structuralLock}
                >
                  {busy ? '…' : 'Add'}
                </Button>
              </div>
              {addValueError !== null ? (
                <span className="block text-body-xs font-body text-terra mt-1">
                  {addValueError}
                </span>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
